import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

import { HorizontalScroll, useScrollEdges } from '../model/use-horizontal-scroll';

/**
 * Page-wise arrows for a table wider than its container — a way to the hidden columns next to
 * wheel/trackpad and drag. A control, not an action (RUID-0003): plain outline `Button`, which matches
 * the toolbar's `view` actions. The caller renders it only while the table overflows.
 */
export function TableScrollControls({ scroll, className }: { scroll: HorizontalScroll; className?: string }) {
  const { t } = useTranslation();
  const { id, scrollPage } = scroll;
  const { canScrollStart, canScrollEnd } = useScrollEdges(scroll);
  const startRef = useRef<HTMLButtonElement>(null);
  const endRef = useRef<HTMLButtonElement>(null);
  /** The arrow a keyboard user pressed, until it reaches its edge or focus moves on */
  const lastPressed = useRef<HTMLButtonElement | null>(null);

  // A keyboard press that takes an arrow to its edge disables it under the user's focus — hand focus to the other arrow
  useEffect(() => {
    const pressed = lastPressed.current;
    if (!pressed?.disabled) return;
    // Once per press: reaching the edge again later by wheel or drag must not pull focus into the toolbar
    lastPressed.current = null;
    // Browsers differ: focus either stays on the disabled button or drops to <body>
    if (document.activeElement !== pressed && document.activeElement !== document.body) return;
    (pressed === startRef.current ? endRef : startRef).current?.focus();
  }, [canScrollStart, canScrollEnd]);

  const arrows = [
    { direction: 'start', ref: startRef, enabled: canScrollStart, label: t('common.scrollLeft'), Icon: ChevronLeft },
    { direction: 'end', ref: endRef, enabled: canScrollEnd, label: t('common.scrollRight'), Icon: ChevronRight }
  ] as const;

  return (
    <ButtonGroup className={className}>
      {arrows.map(({ direction, ref, enabled, label, Icon }) => (
        <Tooltip key={direction}>
          <TooltipTrigger asChild>
            <Button
              ref={ref}
              type="button"
              variant="outline"
              size="icon"
              disabled={!enabled}
              aria-label={label}
              aria-controls={id}
              onClick={(event) => {
                // Keyboard activation (Enter/Space) clicks with detail 0. A mouse user needs no handoff, and the
                // focus it would move opens the other arrow's tooltip under the pointer's nose.
                lastPressed.current = event.detail === 0 ? event.currentTarget : null;
                scrollPage(direction);
              }}
              onBlur={(event) => {
                // Left while still enabled: the user moved on. Losing focus by turning disabled is the case to hand off.
                if (!event.currentTarget.disabled) lastPressed.current = null;
              }}>
              <Icon className="w-4 h-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{label}</TooltipContent>
        </Tooltip>
      ))}
    </ButtonGroup>
  );
}
